import { useEffect } from 'react';
import { create } from 'zustand';

export type ToastTone = 'info' | 'success' | 'warn' | 'danger';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => number;
  dismiss: (id: number) => void;
}

let nextId = 1;

// Lightweight global toast bus. Any module can fire a transient banner via
// the `toast` helpers below without threading callbacks through props.
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, tone = 'info') => {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  info: (m: string) => useToastStore.getState().push(m, 'info'),
  success: (m: string) => useToastStore.getState().push(m, 'success'),
  warn: (m: string) => useToastStore.getState().push(m, 'warn'),
  danger: (m: string) => useToastStore.getState().push(m, 'danger'),
};

const AUTO_DISMISS_MS = 5000;

export function Toasts(): JSX.Element {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div className="toasts" role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t, onDismiss }: { toast: Toast; onDismiss: () => void }): JSX.Element {
  // Auto-dismiss after a fixed delay; clear the timer on unmount so a
  // manually-dismissed toast doesn't fire a stale callback.
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className={`toast toast-${t.tone}`} role="alert">
      <span className="toast-message">{t.message}</span>
      <button type="button" className="toast-close" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}
